import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import {
  Conditional,
  DealerLocatorCancelButton,
  DealerLocatorConfirmButton,
  DealerLocatorTermsAndConditionModal,
  DealerStore,
  DrawerFooter
} from '@cat-ecom/pcc-components';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { useBreakpoint } from 'components/pcc-components';
import { CatLoadingIndicator } from 'blocks-nextjs/blocks-react/components/LoadingIndicator';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { useDealerLocatorLoader } from 'components/dealer-locator/dealer-locator-loader-context';
import { useDealerLocatorModal } from 'components/dealer-locator/dealer-locator-modal-context';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';
import { DealerLocatorTermsWrapper } from './dealer-locator-terms-wrapper';
import { useLogout } from 'hooks/use-logout';
import { SELECTED_STORE_ID, TERMS_CHECKED } from '../constants';
import { useState } from 'react';
import cx from 'classnames';
import { SelectStoreResponse } from 'services/dealer-locator/types';

interface DealerLocatorFooterProps {
  hasSearchedForLocations?: boolean;
  isModal?: boolean;
  showNoStoresMapErrorMessage?: boolean;
  showNetworkErrorMessage: boolean;
  formId: string;
  isSearchingForLocations?: boolean;
  locations?: DealerStore[];
  isSelectStoreError: boolean;
  isWelcomeModal: boolean;
  isStoreLocationTandCFlag?: boolean;
  selectStoreState?: SelectStoreResponse;
}

export const DealerLocatorFooter = ({
  hasSearchedForLocations,
  showNoStoresMapErrorMessage,
  showNetworkErrorMessage,
  isModal = false,
  formId,
  locations,
  isSearchingForLocations,
  isSelectStoreError,
  isWelcomeModal,
  isStoreLocationTandCFlag,
  selectStoreState
}: DealerLocatorFooterProps) => {
  const t = useTranslations();
  const { logout, logoutUrl } = useLogout();
  const { isDealerLocatorLoading } = useDealerLocatorLoader();
  const [isOpen, setIsOpen] = useState(false);
  const dealerLocatorDrawer = useDealerLocatorDrawer();
  const { watch } = useFormContext();

  const selectedStoreId = watch(SELECTED_STORE_ID);
  const selectedStore = locations?.find(
    ({ id }) => id.toString() === selectedStoreId
  );

  const isErrorOrLoading =
    showNoStoresMapErrorMessage ||
    showNetworkErrorMessage ||
    isDealerLocatorLoading ||
    isSearchingForLocations ||
    isSelectStoreError;
  const isSelectStoreButtonDisabled = isErrorOrLoading || !watch(TERMS_CHECKED);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const dealerLocatorModal = useDealerLocatorModal();

  const handleCancel = isModal
    ? dealerLocatorModal.modalControl.close
    : dealerLocatorDrawer.closeDrawer;
  const handleShopAsGuest = () => {
    logout();
    if (!isEmpty(logoutUrl)) {
      window.location.href = logoutUrl;
    }
  };
  const handleModalOpen = () => setIsOpen(true);
  const handleModalCancel = () => setIsOpen(false);

  return (
    <DrawerFooter
      className={cx({
        'text-end': isStoreLocationTandCFlag && !isMD
      })}
    >
      <Conditional test={isStoreLocationTandCFlag && !isMD && !!selectedStore}>
        <DealerLocatorTermsAndConditionModal
          isOpen={isOpen}
          onBlModalCancel={handleModalCancel}
          isDealerLocatorLoading={isDealerLocatorLoading}
          heading={`${t('Footer_Dealer')} ${t('TERMS_CONDITIONS')}`}
        >
          <>
            <DealerLocatorTermsWrapper
              dealerName={selectedStore?.dealerName}
              isDisabled={isErrorOrLoading}
              storeId={selectedStore?.storeId}
              selectStoreState={selectStoreState}
              isSelectStoreError={isSelectStoreError}
              handleShopAsGuest={handleShopAsGuest}
            />
            <DrawerFooter className="text-start">
              <DealerLocatorConfirmButton
                disabled={isErrorOrLoading}
                form={formId}
              >
                {t('YES_I_ACCEPT_TEXT')}
                {isDealerLocatorLoading && (
                  <CatLoadingIndicator small slot="after" />
                )}
              </DealerLocatorConfirmButton>
              <DealerLocatorCancelButton
                onCancel={handleModalCancel}
                isDisabled={isDealerLocatorLoading}
              >
                {t('CANCEL')}
              </DealerLocatorCancelButton>
            </DrawerFooter>
          </>
        </DealerLocatorTermsAndConditionModal>
      </Conditional>
      <Conditional
        test={!(isStoreLocationTandCFlag && !isMD && !!selectedStore)}
        fallback={
          <DealerLocatorConfirmButton
            disabled={!hasSearchedForLocations || isErrorOrLoading}
            onClick={handleModalOpen}
          >
            {t('CAT_SELECT_STORE')}
          </DealerLocatorConfirmButton>
        }
      >
        <Conditional test={!!selectedStore}>
          <DealerLocatorTermsWrapper
            dealerName={selectedStore?.dealerName}
            isDisabled={isErrorOrLoading}
            storeId={selectedStore?.storeId}
          />
        </Conditional>
        <Conditional test={hasSearchedForLocations}>
          <DealerLocatorConfirmButton
            disabled={isSelectStoreButtonDisabled}
            form={formId}
          >
            {t('CAT_SELECT_STORE')}
            {isDealerLocatorLoading && (
              <CatLoadingIndicator small slot="after" />
            )}
          </DealerLocatorConfirmButton>
        </Conditional>
        <Conditional
          test={
            showNoStoresMapErrorMessage ||
            showNetworkErrorMessage ||
            (!hasSearchedForLocations && isWelcomeModal)
          }
          fallback={
            <DealerLocatorCancelButton
              onCancel={handleCancel}
              isDisabled={isDealerLocatorLoading}
            >
              {t('CANCEL')}
            </DealerLocatorCancelButton>
          }
        >
          <CatButton
            type="button"
            variant="ghost"
            className="ps-3"
            onClick={handleShopAsGuest}
          >
            {t('SHOP_AS_GUEST')}
          </CatButton>
        </Conditional>
      </Conditional>
    </DrawerFooter>
  );
};
